<template>
  <div class="gstocky-ai container-fluid py-4">
    <!-- Credits Warning -->
    <div v-if="creditsExhausted" class="alert alert-danger d-flex align-items-center mb-4" role="alert">
      <AppIcon name="bi-exclamation-triangle-fill" class="bi bi-exclamation-triangle-fill me-2" />
      <div>{{ $t('ai_credits_exhausted') }}</div>
    </div>
    <div v-else-if="creditsLow" class="alert alert-warning d-flex align-items-center mb-4" role="alert">
      <AppIcon name="bi-exclamation-triangle" class="bi bi-exclamation-triangle me-2" />
      <div>{{ $t('ai_credits_low') }}</div>
    </div>

    <!-- Header -->
    <div class="ai-header mb-4">
      <div class="d-flex justify-content-between align-items-start flex-wrap gap-3">
        <div>
          <div class="d-flex align-items-center gap-2 mb-2">
            <div class="header-icon">
              <AppIcon name="bi-robot" class="bi bi-robot" />
            </div>
            <h1 class="h3 mb-0 fw-bold">{{ $t('gstocky_ai') }}</h1>
            <span class="badge bg-primary-subtle text-primary">Beta</span>
          </div>
          <p class="text-muted mb-0">{{ $t('ai_assistant_description') }}</p>
        </div>
        <div class="d-flex align-items-center gap-3">
          <!-- Tokens Progress Bar -->
          <div class="tokens-display" :class="{ 'low': creditsLow, 'exhausted': creditsExhausted }">
            <div class="tokens-header">
              <AppIcon name="bi-lightning-charge-fill" class="bi bi-lightning-charge-fill me-1" />
              <span class="fw-semibold">{{ tokensRemaining }}</span>
              <span class="text-muted small ms-1">/ {{ maxTokens }} {{ $t('tokens') }}</span>
            </div>
            <div class="tokens-bar">
              <div class="tokens-bar-fill" :style="{ width: tokenPercentage + '%' }"></div>
            </div>
          </div>
          <div class="d-flex gap-2" v-if="activeMode === 'chat'">
            <button class="btn btn-outline-primary btn-sm" @click="activeMode = 'scan'" :class="{ 'active': activeMode === 'scan' }" :disabled="creditsExhausted">
              <AppIcon name="bi-camera" class="bi bi-camera me-1" />
              {{ $t('scan_invoice') }}
            </button>
            <button class="btn btn-primary btn-sm" @click="startNewConversation" :disabled="creditsExhausted">
              <AppIcon name="bi-plus-lg" class="bi bi-plus-lg me-1" />
              {{ $t('new_conversation') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="row g-4">
      <!-- Sidebar - Conversations -->
      <div class="col-md-3" v-if="activeMode === 'chat'">
        <div class="conversations-panel card shadow-sm">
          <div class="card-header bg-transparent border-0 pb-0">
            <h6 class="mb-0 fw-semibold">{{ $t('conversations') }}</h6>
          </div>
          <div class="card-body p-2">
            <div v-if="conversations.length === 0" class="text-center text-muted py-4">
              <AppIcon name="bi-chat-dots" class="bi bi-chat-dots fs-1 opacity-50" />
              <p class="small mt-2 mb-0">{{ $t('no_conversations') }}</p>
            </div>
            <div v-else class="conversation-list">
              <div 
                v-for="conv in conversations" 
                :key="conv.id"
                class="conversation-item"
                :class="{ 'active': currentConversationId === conv.id }"
                @click="loadConversation(conv.id)"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <span class="conversation-title">{{ conv.title }}</span>
                  <button 
                    class="btn btn-link btn-sm text-danger p-0 opacity-0 delete-btn"
                    @click.stop="deleteConversation(conv.id)"
                  >
                    <AppIcon name="bi-trash" class="bi bi-trash" />
                  </button>
                </div>
                <small class="text-muted">{{ formatDate(conv.updated_at) }}</small>
              </div>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="quick-actions card shadow-sm mt-3">
          <div class="card-header bg-transparent border-0 pb-0">
            <h6 class="mb-0 fw-semibold">{{ $t('quick_actions') }}</h6>
          </div>
          <div class="card-body p-2">
            <button class="quick-action-btn" @click="askQuickQuestion('What were my sales this month?')">
              <AppIcon name="bi-graph-up" class="bi bi-graph-up text-success" />
              <span>{{ $t('sales_summary') }}</span>
            </button>
            <button class="quick-action-btn" @click="askQuickQuestion('Show me products with low stock')">
              <AppIcon name="bi-box-seam" class="bi bi-box-seam text-warning" />
              <span>{{ $t('low_stock_alert') }}</span>
            </button>
            <button class="quick-action-btn" @click="askQuickQuestion('Give me a business overview')">
              <AppIcon name="bi-speedometer2" class="bi bi-speedometer2 text-primary" />
              <span>{{ $t('business_overview') }}</span>
            </button>
            <button class="quick-action-btn" @click="activeMode = 'scan'">
              <AppIcon name="bi-receipt" class="bi bi-receipt text-info" />
              <span>{{ $t('scan_invoice') }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Main Chat Area -->
      <div :class="activeMode === 'chat' ? 'col-md-9' : 'col-12'">
        <!-- Selection Mode -->
        <div v-if="activeMode === 'selection'" class="selection-container h-100 py-3">
          <div class="row g-4 justify-content-center h-100">
            <!-- Scan Invoice Card -->
            <div class="col-md-5 col-lg-4">
              <div 
                class="card h-100 shadow-sm hover-card border-0 cursor-pointer" 
                @click="activeMode = 'scan'"
              >
                <div class="card-body p-5 text-center d-flex flex-column justify-content-center align-items-center">
                  <div class="feature-icon mb-4 bg-primary bg-opacity-10 text-primary p-4 rounded-circle">
                    <AppIcon name="bi-camera-fill" class="bi bi-camera-fill display-4" />
                  </div>
                  <h3 class="fw-bold mb-3">{{ $t('scan_invoice') }}</h3>
                  <p class="text-muted mb-0">{{ $t('scan_invoice_description', 'Automatically extract data from your invoices using AI.') }}</p>
                </div>
              </div>
            </div>


          </div>
        </div>
        <!-- Scan Mode -->
        <div v-if="activeMode === 'scan'" class="scan-container card shadow-sm p-4 h-100">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h5 class="fw-bold mb-0">
              <AppIcon name="bi-camera" class="bi bi-camera me-2 text-primary" />{{ $t('scan_invoice') }}
            </h5>
          </div>

          <div class="row justify-content-center">
            <div class="col-md-10">


              <!-- Upload Area -->
              <div 
                class="upload-area p-4 mb-3 d-flex flex-column justify-content-center"
                :class="{ 'drag-over': isDragOver }"
                @dragover.prevent="isDragOver = true"
                @dragleave.prevent="isDragOver = false"
                @drop.prevent="handleDrop"
                @click="$refs.fileInput.click()"
              >
                <input 
                  type="file" 
                  ref="fileInput" 
                  class="d-none" 
                  accept="image/*,application/pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  @change="handleFileSelect"
                />
                <div v-if="!scanPreview" class="upload-placeholder text-center py-5">
                  <AppIcon name="bi-cloud-upload" class="bi bi-cloud-upload fs-1 text-primary" />
                  <p class="mt-3 mb-1 fw-semibold">{{ $t('drop_invoice_here') }}</p>
                  <p class="text-muted small">{{ $t('or_click_to_upload') }}</p>
                  <p class="text-muted small mt-2">Supports PDF, DOCX, PNG, JPG</p>
                </div>
                <div v-else class="preview-container text-center position-relative h-100 d-flex align-items-center justify-content-center">
                   <div v-if="isScanning" class="scanning-overlay rounded">
                      <div class="scanning-beam"></div>
                   </div>
                   <img :src="scanPreview" alt="Invoice preview" class="img-fluid rounded shadow-sm" style="max-height: 400px; object-fit: contain;" />
                   <button v-if="!isScanning" class="btn btn-sm btn-danger position-absolute top-0 end-0 m-2 rounded-circle shadow" @click.stop="clearScanPreview">
                     <AppIcon name="bi-x-lg" class="bi bi-x-lg" />
                   </button>
                 </div>
              </div>

              <!-- Action Button -->
              <div class="text-center mt-4">
                <button 
                  v-if="scanPreview"
                  type="button" 
                  class="btn btn-primary btn-lg px-5 py-3 rounded-pill fw-bold shadow-sm" 
                  @click="performScan"
                  :disabled="isScanning"
                >
                  <span v-if="isScanning" class="spinner-border spinner-border-sm me-2"></span>
                  <AppIcon name="bi-magic" v-else class="bi bi-magic me-2" />{{ isScanning ? $t('scanning') : $t('scan_now') }}
                </button>
              </div>
            </div>
          </div>
        </div>
          
        <!-- Mapping Mode -->
        <div v-if="activeMode === 'mapping'" class="mapping-container card shadow-sm p-4 h-100">
          <div class="d-flex justify-content-between align-items-center mb-4">
            <h5 class="fw-bold mb-0">
              <AppIcon name="bi-list-check" class="bi bi-list-check me-2 text-primary" />{{ $t('review_extracted_data', 'Review Extracted Data') }}
            </h5>
            <button class="btn btn-outline-secondary btn-sm" @click="activeMode = 'scan'">
              <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-1" />
              {{ $t('back') }}
            </button>
          </div>

          <div v-if="mappingData && scanResult" class="row">
            <!-- Left: Invoice Image -->
            <div class="col-lg-6 mb-4">
              <div class="sticky-top" style="top: 20px; z-index: 5;">
                <div class="card border-0 shadow-sm bg-dark rounded-4 overflow-hidden">
                  <div class="card-header bg-dark text-white border-0 py-3 d-flex justify-content-between align-items-center">
                    <span class="fw-bold small text-uppercase"><AppIcon name="bi-file-earmark-image" class="bi bi-file-earmark-image me-2" />Invoice Preview</span>
                    <div class="badge bg-primary px-3">{{ mappingContext === 'client' ? $t('for_client_invoice') : $t('for_supplier_invoice') }}</div>
                  </div>
                  <div class="card-body p-0 bg-secondary bg-opacity-10 d-flex align-items-center justify-content-center" style="min-height: 70vh;">
                     <img :src="scanPreview" alt="Invoice" class="img-fluid" style="object-fit: contain; max-height: 85vh;" />
                  </div>
                </div>
              </div>
            </div>

            <!-- Right: Review Data -->
            <div class="col-lg-6">
              <!-- Invoice Header Summary -->
              <div class="card mb-4 border-0 shadow-sm bg-primary bg-opacity-10 rounded-4">
                <div class="card-body p-4">
                  <div class="row g-3">
                    <div class="col-sm-4">
                      <div class="small text-muted text-uppercase fw-bold mb-1">{{ $t('date') }}</div>
                      <div class="fw-bold fs-5">{{ scanResult.date || '-' }}</div>
                    </div>
                    <div class="col-sm-4 border-start border-end border-white border-opacity-50">
                      <div class="small text-muted text-uppercase fw-bold mb-1">{{ $t('reference') }}</div>
                      <div class="fw-bold fs-5 text-truncate" :title="scanResult.reference">{{ scanResult.reference || '-' }}</div>
                    </div>
                    <div class="col-sm-4">
                      <div class="small text-muted text-uppercase fw-bold mb-1">{{ $t('total') }}</div>
                      <div class="fw-bold fs-5 text-success">{{ formatMoney(scanResult.total) }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Contact Review -->
              <div v-if="mappingContext === 'client'" class="card mb-4 border-0 shadow-sm">
                <div class="card-header bg-white border-bottom">
                  <h6 class="mb-0 fw-bold">
                    <AppIcon name="bi-person-badge" class="bi bi-person-badge me-2 text-primary" />
                    {{ mappingContext === 'client' ? $t('client') : $t('supplier') }}
                  </h6>
                </div>
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center">
                    <div>
                      <span class="fs-5 fw-semibold">{{ mappingData.contact.name }}</span>
                      <div class="mt-1">
                        <span v-if="mappingData.contact.exists" class="badge bg-success bg-opacity-10 text-success">
                          <AppIcon name="bi-check-circle" class="bi bi-check-circle me-1" />{{ $t('existing_contact', 'Existing Contact') }}
                        </span>
                        <span v-else class="badge bg-warning bg-opacity-10 text-warning">
                          <AppIcon name="bi-exclamation-triangle" class="bi bi-exclamation-triangle me-1" />{{ $t('new_contact', 'New Contact') }}
                        </span>
                      </div>
                    </div>
                    
                    <div v-if="!mappingData.contact.exists" class="form-check form-switch fs-5">
                      <input class="form-check-input mt-0" type="checkbox" role="switch" :id="'createContactSwitch'" v-model="mappingData.contact.shouldCreate">
                      <label class="form-check-label fs-6 ms-2" :for="'createContactSwitch'">
                        {{ $t('add_to_database', 'Save to database') }}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Products Review -->
              <div class="card mb-4 border-0 shadow-sm">
                <div class="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                  <h6 class="mb-0 fw-bold">
                    <AppIcon name="bi-box-seam" class="bi bi-box-seam me-2 text-primary" />
                    {{ $t('products') }} ({{ mappingData.products.length }})
                  </h6>
                  <span class="text-muted small">{{ $t('matching_items', 'Matching items with inventory') }}</span>
                </div>
                
                <ul class="list-group list-group-flush">
                  <li v-for="(prod, idx) in mappingData.products" :key="idx" class="list-group-item py-3">
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="w-100">
                        <div class="d-flex justify-content-between mb-1">
                          <span class="fw-semibold">{{ prod.label }}</span>
                          <span class="fw-bold">{{ formatMoney(prod.scannedPrice) }} <small class="text-muted fw-normal">x {{ prod.scannedQuantity }}</small></span>
                        </div>
                        
                        <div class="d-flex justify-content-between align-items-center mt-2">
                          <div class="status-badge">
                            <span v-if="prod.exists" class="badge bg-success bg-opacity-10 text-success">
                              <AppIcon name="bi-check-circle" class="bi bi-check-circle me-1" />{{ $t('existing_product', 'In Stock') }}
                            </span>
                            <span v-else class="badge bg-warning bg-opacity-10 text-warning">
                              <AppIcon name="bi-exclamation-triangle" class="bi bi-exclamation-triangle me-1" />{{ $t('new_product', 'New Product') }}
                            </span>
                          </div>
                          
                          <div v-if="!prod.exists" class="form-check form-switch">
                            <input class="form-check-input" type="checkbox" role="switch" :id="'createProductSwitch' + idx" v-model="prod.shouldCreate">
                            <label class="form-check-label small" :for="'createProductSwitch' + idx">
                              {{ $t('add_to_stock', 'Add to Stock') }}
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li v-if="mappingData.products.length === 0" class="list-group-item py-4 text-center text-muted">
                    {{ $t('no_products_found', 'No products could be extracted.') }}
                  </li>
                </ul>
              </div>

              <!-- Action Buttons -->
              <div class="d-flex justify-content-end gap-3 mt-4">
                <button type="button" class="btn btn-light" @click="activeMode = 'scan'" :disabled="isMapping">
                  {{ $t('cancel') }}
                </button>
                <button type="button" class="btn btn-success px-4" @click="confirmMapping" :disabled="isMapping">
                  <span v-if="isMapping" class="spinner-border spinner-border-sm me-2"></span>
                  <AppIcon name="bi-check2-all" v-else class="bi bi-check2-all me-1" />
                  {{ $t('confirm_and_proceed', 'Confirm & Proceed') }}
                </button>
              </div>
            </div>
          </div>
        </div>        <!-- Chat Mode -->
        <div v-if="activeMode === 'chat'" class="chat-container card shadow-sm">
          <!-- Messages -->
          <div class="chat-messages" ref="messagesContainer">
            <div v-if="messages.length === 0" class="empty-chat">
              <div class="empty-chat-content">
                <div class="ai-avatar-large">
                  <AppIcon name="bi-robot" class="bi bi-robot" />
                </div>
                <h4 class="mt-4 fw-bold">{{ $t('welcome_to_gstocky_ai') }}</h4>
                <p class="text-muted">{{ $t('ai_welcome_message') }}</p>
                <div class="suggestion-chips">
                  <button class="suggestion-chip" @click="askQuickQuestion('What were my sales this month?')">
                    <AppIcon name="bi-graph-up" class="bi bi-graph-up" /> {{ $t('sales_this_month') }}
                  </button>
                  <button class="suggestion-chip" @click="askQuickQuestion('Which products need restocking?')">
                    <AppIcon name="bi-box-seam" class="bi bi-box-seam" /> {{ $t('inventory_check') }}
                  </button>
                  <button class="suggestion-chip" @click="askQuickQuestion('Give me business insights')">
                    <AppIcon name="bi-lightbulb" class="bi bi-lightbulb" /> {{ $t('get_insights') }}
                  </button>
                </div>
              </div>
            </div>
            
            <div v-else>
              <div 
                v-for="(msg, index) in messages" 
                :key="index"
                class="message"
                :class="msg.role"
              >
                <div class="message-avatar">
                  <AppIcon :name="msg.role === 'user' ? 'bi bi-person' : 'bi bi-robot'" :class="msg.role === 'user' ? 'bi bi-person' : 'bi bi-robot'" />
                </div>
                <div class="message-content">
                  <div class="message-bubble" v-html="formatMessage(msg.content)"></div>
                  <small class="message-time text-muted">{{ formatTime(msg.created_at) }}</small>
                </div>
              </div>
              
              <!-- Typing indicator -->
              <div v-if="isLoading" class="message assistant">
                <div class="message-avatar">
                  <AppIcon name="bi-robot" class="bi bi-robot" />
                </div>
                <div class="message-content">
                  <div class="message-bubble typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Input Area -->
          <div class="chat-input-container">
            <div class="input-wrapper">
              <textarea
                v-model="inputMessage"
                :placeholder="$t('ask_ai_placeholder')"
                @keydown.enter.exact.prevent="sendMessage"
                rows="1"
                :disabled="isLoading"
                ref="inputField"
              ></textarea>
              <button 
                class="send-btn" 
                @click="sendMessage" 
                :disabled="!inputMessage.trim() || isLoading"
              >
                <AppIcon name="bi-send-fill" class="bi bi-send-fill" />
              </button>
            </div>
            <p class="input-hint text-muted text-center mt-2 mb-0">
              <small>{{ $t('ai_hint') }}</small>
            </p>
          </div>
      </div>
    </div>
  </div>


  
  <!-- Modals -->
  <add-edit-client-invoice-modal 
    v-if="createClientInvoiceTarget" 
    action="create"
    :is-modal-open="true"
    :data="clientInvoice" 
    @createInvoice="handleClientInvoiceCreated"
  />
  <add-edit-supplier-invoice-modal 
    v-if="createSupplierInvoiceTarget" 
    action="create"
    :is-modal-open="true"
    :data="supplierInvoice" 
    @createInvoice="handleSupplierInvoiceCreated"
  />
  </div>
</template>

<script>
import helper from '@/helper'
import { convertInvoiceFileToImage, isSupportedInvoiceFile } from '@/utils/invoiceFile'
import AddEditClientInvoiceModal from '@/components/invoice/AddEditInvoiceModal';
import AddEditSupplierInvoiceModal from '@/components/supplierInvoice/AddEditInvoiceModal';
import { mapGetters } from 'vuex'
import moment from 'moment'

export default {
  name: 'GstockyAI',
  components: {
    AddEditClientInvoiceModal,
    AddEditSupplierInvoiceModal
  },
  data() {
    return {
      inputMessage: '',
      messages: [],
      conversations: [],
      currentConversationId: null,
      isLoading: false,
      isDragOver: false,
      scanPreview: null,
      scanResult: null,
      isScanning: false,
      scanFile: null,
      activeMode: 'scan', // 'chat' or 'scan' or 'selection' or 'mapping'
      
      // Modal Data
      clientInvoice: {},
      supplierInvoice: {},
      createClientInvoiceTarget: false,
      createSupplierInvoiceTarget: false,
      
      creditBalance: 2.00,
      totalCreditsUsed: 0,
      maxCredits: 2.00,  // Initial credit amount
      
      // Mapping State
      mappingData: null,
      mappingContext: 'supplier', // 'client' or 'supplier'
      isMapping: false
    }
  },
  computed: {
    maxTokens() {
      // Convert credits to tokens: $0.01 = 1 token, so $2.00 = 200 tokens
      return Math.round(this.maxCredits * 100)
    },
    tokensRemaining() {
      return Math.max(0, Math.round(this.creditBalance * 100))
    },
    tokenPercentage() {
      return Math.max(0, Math.min(100, (this.tokensRemaining / this.maxTokens) * 100))
    },
    creditsExhausted() {
      return this.creditBalance <= 0
    },
    creditsLow() {
      return this.creditBalance > 0 && this.creditBalance < 0.50
    },
    ...mapGetters({
        business : 'BUSINESS'
    })
  },
  mounted() {
    const access = helper.checkAccess('view_ai')
    if (access === -2) return this.$router.push({ path: '/subscribe' })
    if (access === -1) return this.$router.push({ path: '/' })

    this.loadCredits()
    this.loadConversations()
  },
  methods: {
    formatMoney(val) {
      return helper.formatMoney(val);
    },
    async loadCredits() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/ai/credits`)
        this.creditBalance = response.data.credit_balance
        this.totalCreditsUsed = response.data.total_credits_used
      } catch (error) {
        alert('Failed to load credits:', error)
      }
    },
    async loadConversations() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/ai/conversations`)
        this.conversations = response.data
      } catch (error) {
        alert('Failed to load conversations:', error)
      }
    },
    
    async loadConversation(id) {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/ai/conversations/${id}`)
        this.currentConversationId = id
        this.messages = response.data.messages || []
        this.$nextTick(() => this.scrollToBottom())
      } catch (error) {
        helper.handleError(error)
      }
    },
    
    async sendMessage() {
      if (!this.inputMessage.trim() || this.isLoading) return
      
      // Check if credits exhausted
      if (this.creditsExhausted) {
        return
      }
      
      const message = this.inputMessage.trim()
      this.inputMessage = ''
      
      // Add user message to UI immediately
      this.messages.push({
        role: 'user',
        content: message,
        created_at: new Date().toISOString()
      })
      
      this.isLoading = true
      this.scrollToBottom()
      
      try {
        const response = await helper.customPost(`${process.env.VUE_APP_API}/ai/chat`, {
          message,
          conversation_id: this.currentConversationId
        })
        
        // Update conversation ID if new
        if (!this.currentConversationId) {
          this.currentConversationId = response.data.conversation_id
          this.loadConversations()
        }
        
        // Add assistant response
        this.messages.push({
          role: 'assistant',
          content: response.data.message,
          created_at: new Date().toISOString()
        })
        
        // Update credit balance from response
        if (response.data.credits_remaining !== undefined) {
          this.creditBalance = response.data.credits_remaining
        }
        
      } catch (error) {
        // Check if credits exhausted error
        if (error.response?.data?.code === 'CREDITS_EXHAUSTED') {
          this.creditBalance = 0
        }
        helper.handleError(error)
        // Remove user message on error
        this.messages.pop()
      } finally {
        this.isLoading = false
        this.scrollToBottom()
      }
    },
    
    async deleteConversation(id) {
      if (!confirm(this.$t('confirm_delete_conversation'))) return
      
      try {
        await helper.customDelete(`${process.env.VUE_APP_API}/ai/conversations/${id}`)
        this.conversations = this.conversations.filter(c => c.id !== id)
        if (this.currentConversationId === id) {
          this.currentConversationId = null
          this.messages = []
        }
      } catch (error) {
        helper.handleError(error)
      }
    },
    
    startNewConversation() {
      this.currentConversationId = null
      this.messages = []
      this.$nextTick(() => this.$refs.inputField?.focus())
    },
    
    askQuickQuestion(question) {
      this.inputMessage = question
      this.sendMessage()
    },
    
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    },
    
    formatMessage(content) {
      if (!content) return ''
      // Enhanced markdown-like formatting
      let formatted = content
        // Bold
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        // Italic
        .replace(/\*(.*?)\*/g, '<em>$1</em>')
        // Inline code
        .replace(/`(.*?)`/g, '<code>$1</code>')
        // Headings (### H3, ## H2)
        .replace(/^### (.*$)/gm, '<h6 class="mt-2 mb-1 fw-bold">$1</h6>')
        .replace(/^## (.*$)/gm, '<h5 class="mt-2 mb-1 fw-bold">$1</h5>')
        // Horizontal rule
        .replace(/^---$/gm, '<hr class="my-2">')

      // Convert bullet lists (- item or * item)
      formatted = formatted.replace(/^[-*] (.+)$/gm, '<li>$1</li>')
      formatted = formatted.replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul class="mb-2 ps-3">$1</ul>')

      // Convert numbered lists (1. item)
      formatted = formatted.replace(/^\d+\.\s(.+)$/gm, '<li>$1</li>')
      // Wrap consecutive <li> not already in <ul> into <ol>
      formatted = formatted.replace(/((?:<li>.*<\/li>\n?)+)/g, (match) => {
        if (match.includes('<ul')) return match
        return '<ol class="mb-2 ps-3">' + match + '</ol>'
      })

      // Line breaks (for remaining newlines)
      formatted = formatted.replace(/\n/g, '<br>')
      // Clean up extra <br> after block elements
      formatted = formatted.replace(/<\/(ul|ol|h[1-6]|hr)><br>/g, '</$1>')

      return formatted
    },
    
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString()
    },
    
    formatTime(date) {
      if (!date) return ''
      return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },
    
    // Scan Invoice Methods
    handleDrop(e) {
      this.isDragOver = false
      const file = e.dataTransfer.files[0]
      if (file) this.processFile(file)
    },
    
    handleFileSelect(e) {
      const file = e.target.files[0]
      if (file) {
        this.processFile(file)
      }
    },
    
    async processFile(file) {
      this.scanFile = file
      
      try {
        if (!isSupportedInvoiceFile(file)) throw new Error('Unsupported file type. Please upload a PDF, DOCX, PNG, or JPG file.')
        this.scanPreview = await convertInvoiceFileToImage(file)
        this.scanResult = null
      } catch (error) {
        this.scanFile = null
        helper.handleError(error)
      }
    },
    
    clearScanPreview() {
      this.scanPreview = null
      this.scanResult = null
      this.scanFile = null
    },
    
    async performScan() {
      if (!this.scanPreview) return
      
      this.isScanning = true
      
      try {
        const response = await helper.customPost(`${process.env.VUE_APP_API}/ai/scan-invoice`, { image: this.scanPreview })
        this.scanResult = response.data
        
        // Immediately trigger analysis based on upfront selection
        const analysis = await helper.analyzeScannedEntities(this.scanResult, this.mappingContext);
        this.mappingData = analysis;
        this.activeMode = 'mapping';
      } catch (error) {
        helper.handleError(error)
      } finally {
        this.isScanning = false
      }
    },
    
    async createSupplierInvoice() {
      this.isScanning = true; 
      try {
          const analysis = await helper.analyzeScannedEntities(this.scanResult, 'supplier');
          this.mappingData = analysis;
          this.mappingContext = 'supplier';
          this.activeMode = 'mapping';
      } catch(e) {
          helper.handleError(e);
      } finally {
          this.isScanning = false;
      }
    },
    
    async createClientInvoice() {
      this.isScanning = true;
      try {
          const analysis = await helper.analyzeScannedEntities(this.scanResult, 'client');
          this.mappingData = analysis;
          this.mappingContext = 'client';
          this.activeMode = 'mapping';
      } catch(e) {
          alert(e);
          helper.handleError(e);
      } finally {
          this.isScanning = false;
      }
    },

    async confirmMapping() {
        if (!this.mappingData || !this.mappingContext) return;
        this.isMapping = true;
        
        try {
            // 1. Process Contact
            const dataToMap = JSON.parse(JSON.stringify(this.scanResult));
            
            if (this.mappingData.contact.exists && this.mappingData.contact.existingEntity) {
               if (this.mappingContext === 'client') {
                   dataToMap.existing_client = this.mappingData.contact.existingEntity;
               } else {
                   dataToMap.notes = `${dataToMap.notes || ''}\nExtracted Name: ${this.mappingData.contact.name}`.trim();
               }
            } else if (!this.mappingData.contact.exists && this.mappingData.contact.shouldCreate) {
                // Create Contact
                const name = this.mappingData.contact.name;
                if (this.mappingContext === 'client') {
                   const newClient = { name: name, phone: '0000000000', notes: 'Auto-created from scan invoice.' };
                   const createResp = await helper.customPost(`${process.env.VUE_APP_API}/clients`, newClient);
                   dataToMap.existing_client = createResp.data.client || createResp.data;
                } else {
                   dataToMap.notes = `${dataToMap.notes || ''}\nExtracted Name: ${name}`.trim();
                }
            }

            // 2. Process Products
            dataToMap.products = [];
            for (let i = 0; i < this.mappingData.products.length; i++) {
                const pAnalysis = this.mappingData.products[i];
                let p = JSON.parse(JSON.stringify(pAnalysis.originalData));
                
                if (pAnalysis.exists && pAnalysis.existingEntity) {
                    const inv = pAnalysis.existingEntity;
                    if (this.mappingContext === 'client') {
                         p.inventory_id = inv.id;
                         p.product_id = inv.product ? inv.product.id : inv.product_id;
                         p.id = p.product_id;
                         p.label = inv.product ? inv.product.label : pAnalysis.label;
                         p.vat = inv.product ? inv.product.vat : (inv.vat || 0);
                    } else {
                         // Supplier fetches directly from Products
                         p.product_id = inv.id;
                         p.id = inv.id;
                         p.vat = inv.vat || 0;
                    }
                } else if (!pAnalysis.exists && pAnalysis.shouldCreate) {
                    // Create Product
                    const newProduct = {
                        label: pAnalysis.label,
                        price: pAnalysis.scannedPrice,
                        purchase_price: 0,
                        vat: 0,
                        quantity: pAnalysis.scannedQuantity || 0,
                        notes: 'Auto-created from scan invoice',
                        has_variants: false,
                        variants: []
                    };
                    const createResp = await helper.customPost(`${process.env.VUE_APP_API}/products`, newProduct);
                    const created = createResp.data.product || createResp.data;
                    p.product_id = created.id;
                    p.id = created.id;
                    p.vat = created.vat;
                } else {
                    // Exists == false, shouldCreate == false. Keep it custom!
                    // Add property so mapToInvoice knows it is custom and doesn't assign product_id
                    p.custom_product = true; 
                }
                
                dataToMap.products.push(p);
            }

            // 3. Map to Invoice and Open Modal
            if (this.mappingContext === 'client') {
                this.clientInvoice = this.mapToInvoice(dataToMap, 'client');
                this.clientInvoice.scannedFile = this.scanFile;
                this.createClientInvoiceTarget = true;
            } else {
                this.supplierInvoice = this.mapToInvoice(dataToMap, 'supplier');
                this.supplierInvoice.scannedFile = this.scanFile;
                this.createSupplierInvoiceTarget = true;
            }

            // 4. Cleanup UI State
            this.scanResult = null;
            this.scanPreview = null;
            this.scanFile = null;
            this.mappingData = null;
            this.mappingContext = 'supplier';
            if (this.$refs.fileInput) this.$refs.fileInput.value = '';
            // Return to scan screen instead of getting stuck on mapping
            this.activeMode = 'scan'; 

        } catch (e) {
             helper.handleError(e);
        } finally {
             this.isMapping = false;
        }
    },


    mapToInvoice(data, type) {
        const invoice = {
            id: null,
            products: [],
            date: data.date || moment().format('YYYY-MM-DD'),
            due_date: data.due_date || moment().add(30, 'days').format('YYYY-MM-DD'), // Default
            notes: data.notes || '',
            discount: 0,
            shipping_amount: 0,
            status: 'draft',
            payment_terms: '',
            terms_and_conditions: ''
        };

        if(data.reference) invoice.reference = data.reference;

        if (type === 'client') {
            if(data.existing_client) {
                invoice.client = data.existing_client;
                invoice.client_id = data.existing_client.id;
            } else if(data.supplier_name) {
                 invoice.notes = (invoice.notes || '') + `\nExtracted Name: ${data.supplier_name}`;
            }
        } else if(data.supplier_name && !invoice.notes?.includes(data.supplier_name)) {
            invoice.notes = (invoice.notes || '') + `\nExtracted Name: ${data.supplier_name}`;
        }

        if(data.products && Array.isArray(data.products) && data.products.length > 0){
            invoice.products = data.products.map(p => ({
                label: p.label || p.name || 'Unknown Product',
                product_label: p.label || p.name || 'Unknown Product',
                quantity: p.quantity || 1,
                price: this.cleanPrice(p.price),
                discount: 0,
                vat: p.vat || this.business.vat,
                total: this.cleanPrice(p.total),
                sub_total: this.cleanPrice(p.sub_total),
                custom_product: !p.product_id,
                preventSuggestions: true,
                id: p.id || null,
                product_id: p.product_id || null,
                inventory_id: p.inventory_id || null
            }));
        } else {
             invoice.products.push({
                label : '',
                id : null,
                quantity : 0,
                price : 0,
                discount : 0,
                vat : this.business.vat,
                total : 0,
                sub_total : 0,
                preventSuggestions: true,
            })
        }
        
        invoice.isScanned = true; // Flag for modal behaviors if any
        return invoice;
    },

    handleClientInvoiceCreated(data) {
        if(data && data.state === false) {
            this.createClientInvoiceTarget = false;
            // Success?
            this.$toast?.success ? this.$toast.success(this.$t('successfully_saved')) : null;
        }
    },
    
    handleSupplierInvoiceCreated(data) {
         if(data && data.state === false) {
            this.createSupplierInvoiceTarget = false;
             this.$toast?.success ? this.$toast.success(this.$t('successfully_saved')) : null;
        }
    },

    cleanPrice(val) {
        if (typeof val === 'number') return val;
        if (!val) return 0;
        // Keep digits and ONE dot. 
        // Simple approach: Remove non-digits/dots. 
        // If "3,500.00", remove comma -> "3500.00".
        return parseFloat(val.toString().replace(/[^0-9.]/g, '')) || 0;
    }
  }
}
</script>

<style scoped>
.gstocky-ai {
  min-height: calc(100vh - 120px);
}

/* Tokens Display */
.tokens-display {
  display: flex;
  flex-direction: column;
  min-width: 160px;
}

.tokens-header {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  margin-bottom: 4px;
}

.tokens-header i {
  color: #6366f1;
}

.tokens-bar {
  height: 6px;
  background: var(--bs-gray-200);
  border-radius: 3px;
  overflow: hidden;
}

.tokens-bar-fill {
  height: 100%;
  background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.tokens-display.low .tokens-bar-fill {
  background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
}

.tokens-display.low .tokens-header i {
  color: #f59e0b;
}

.tokens-display.exhausted .tokens-bar-fill {
  background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
}

.tokens-display.exhausted .tokens-header i {
  color: #ef4444;
}

.header-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
}

.hover-card {
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}

.hover-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 .5rem 1rem rgba(0,0,0,.15)!important;
}

.grayscale {
  filter: grayscale(1);
}

.feature-icon {
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

/* Scanning Animation */
.scanning-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 0.375rem;
  z-index: 10;
  pointer-events: none;
}

.scanning-beam {
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, transparent 40%, rgba(99, 102, 241, 0.4) 50%, transparent 60%);
  animation: scan 2s linear infinite;
  box-shadow: 0 0 15px rgba(99, 102, 241, 0.5);
}

@keyframes scan {
  0% { transform: translateY(-100%); }
  100% { transform: translateY(100%); }
}

/* Conversations Panel */
.conversations-panel {
  max-height: 400px;
  overflow-y: auto;
}

.conversation-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.conversation-item {
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.conversation-item:hover {
  background: var(--bs-gray-100);
}

.conversation-item:hover .delete-btn {
  opacity: 1;
}

.conversation-item.active {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
}

.conversation-item.active .text-muted {
  color: rgba(255,255,255,0.7) !important;
}

.conversation-title {
  font-size: 0.875rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 180px;
  display: block;
}

/* Quick Actions */
.quick-action-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 8px;
  text-align: left;
  transition: all 0.2s ease;
  font-size: 0.875rem;
}

.quick-action-btn:hover {
  background: var(--bs-gray-100);
}

.quick-action-btn i {
  font-size: 1.1rem;
}

/* Chat Container */
.chat-container {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 200px);
  min-height: 500px;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
}

/* Empty Chat */
.empty-chat {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

.empty-chat-content {
  text-align: center;
  max-width: 400px;
}

.ai-avatar-large {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 2.5rem;
  margin: 0 auto;
}

.suggestion-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  margin-top: 20px;
}

.suggestion-chip {
  padding: 8px 16px;
  border: 1px solid var(--bs-gray-300);
  border-radius: 20px;
  background: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.suggestion-chip:hover {
  border-color: #6366f1;
  color: #6366f1;
  background: #f0f0ff;
}

/* Messages */
.message {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
}

.message.user {
  flex-direction: row-reverse;
}

.message-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.message.user .message-avatar {
  background: var(--bs-gray-200);
  color: var(--bs-gray-700);
}

.message.assistant .message-avatar {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
}

.message-content {
  max-width: 70%;
}

.message.user .message-content {
  text-align: right;
}

.message-bubble {
  padding: 12px 16px;
  border-radius: 16px;
  display: inline-block;
  text-align: left;
}

.message.user .message-bubble {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  border-bottom-right-radius: 4px;
}

.message.assistant .message-bubble {
  background: var(--bs-gray-100);
  color: var(--bs-gray-800);
  border-bottom-left-radius: 4px;
}

.message-time {
  display: block;
  font-size: 0.75rem;
  margin-top: 4px;
}

/* Typing Indicator */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 16px 20px !important;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  background: var(--bs-gray-400);
  border-radius: 50%;
  animation: typing 1.4s infinite ease-in-out both;
}

.typing-indicator span:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator span:nth-child(2) { animation-delay: -0.16s; }

@keyframes typing {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

/* Input Area */
.chat-input-container {
  padding: 16px 20px;
  border-top: 1px solid var(--bs-gray-200);
}

.input-wrapper {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.input-wrapper textarea {
  flex: 1;
  border: 1px solid var(--bs-gray-300);
  border-radius: 24px;
  padding: 12px 20px;
  resize: none;
  font-size: 0.95rem;
  max-height: 120px;
  transition: border-color 0.2s ease;
}

.input-wrapper textarea:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
}

.send-btn {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.send-btn:hover:not(:disabled) {
  transform: scale(1.05);
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.input-hint {
  font-size: 0.75rem;
}

/* Modal */
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 1040;
}

.modal-dialog {
  z-index: 1050;
  position: relative;
}

/* Upload Area */
.upload-area {
  border: 2px dashed var(--bs-gray-300);
  border-radius: 12px;
  padding: 40px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.upload-area:hover,
.upload-area.drag-over {
  border-color: #6366f1;
  background: #f8f8ff;
}

.preview-container {
  position: relative;
  overflow: hidden;
}

.preview-container img {
  max-height: 300px;
}

.remove-preview {
  position: absolute;
  top: 10px;
  right: 10px;
}

/* Enhanced Scan Animation */
.scanning-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(99, 102, 241, 0.1);
  z-index: 10;
  overflow: hidden;
  backdrop-filter: blur(1px);
}

.scanning-beam {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 8px;
  background: linear-gradient(to bottom, 
    rgba(99, 102, 241, 0) 0%, 
    rgba(99, 102, 241, 0.8) 50%, 
    rgba(99, 102, 241, 1) 100%);
  box-shadow: 0 0 15px 3px rgba(99, 102, 241, 0.6);
  animation: scan 2s linear infinite;
  z-index: 11;
}

.scanning-overlay::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(to bottom, 
    rgba(99, 102, 241, 0.2) 0%, 
    transparent 100%);
  animation: scanPulse 2s linear infinite;
}

@keyframes scan {
  0% { transform: translateY(-100%); }
  100% { transform: translateY(5000%); /* Accounts for taller images */ }
}

@keyframes scanPulse {
  0% { opacity: 0; height: 0; }
  50% { opacity: 1; height: 100%; }
  100% { opacity: 0; height: 100%; }
}

/* Scan Results */
.scan-results {
  background: var(--bs-gray-50);
  border-radius: 12px;
  padding: 20px;
}

/* Custom Radio Styling */
.custom-radio .form-check-label {
  transition: all 0.2s ease;
  border-width: 2px !important;
}

.custom-radio .form-check-label:hover {
  background-color: var(--bs-gray-100);
}

.cursor-pointer {
  cursor: pointer;
}
</style>
